import { CAMPAIGN_OPERATIONS_PHASE_STATUSES } from "@/marketing/models/campaignOperations";

export type TrackerColumnGroup = "campaign" | "onboarding" | "content" | "government";

export type TrackerEditorKind = "text" | "longText" | "link" | "date" | "status" | "boolean" | "pic";

export type TrackerComputedKind = "deadline" | "brand" | "campaign" | "status" | "type" | "onboarded" | "posted" | "pace";

export interface TrackerEditableColumn {
  id: string;
  group: TrackerColumnGroup;
  header: string;
  field: string;
  editor: TrackerEditorKind;
  options?: readonly string[];
}

export interface TrackerComputedColumn {
  id: string;
  group: TrackerColumnGroup;
  header: string;
  computed: TrackerComputedKind;
}

export type TrackerColumn = TrackerEditableColumn | TrackerComputedColumn;

export const TRACKER_GROUP_LABELS: Record<TrackerColumnGroup, string> = {
  campaign: "Campaign Details",
  onboarding: "Scouting / Onboarding Team",
  content: "Content Team",
  government: "Government Report",
};

const STATUS = CAMPAIGN_OPERATIONS_PHASE_STATUSES;

/** Master-sheet column layouts for the standard and paid tracker tabs. */
export class TrackerColumns {
  private static editable(group: TrackerColumnGroup, header: string, field: string, editor: TrackerEditorKind, options?: readonly string[]): TrackerEditableColumn {
    return { id: field, group, header, field, editor, ...(options ? { options } : {}) };
  }

  private static computed(group: TrackerColumnGroup, header: string, kind: TrackerComputedKind): TrackerComputedColumn {
    return { id: kind, group, header, computed: kind };
  }

  private static readonly CAMPAIGN_DETAILS: TrackerColumn[] = [
    TrackerColumns.computed("campaign", "Deadline", "deadline"),
    TrackerColumns.computed("campaign", "Brand", "brand"),
    TrackerColumns.computed("campaign", "Campaign", "campaign"),
    TrackerColumns.computed("campaign", "Status", "status"),
    TrackerColumns.computed("campaign", "Type", "type"),
    TrackerColumns.editable("campaign", "Brand PIC", "pics.brand", "pic"),
    TrackerColumns.editable("campaign", "Onboarding PIC", "pics.scouting", "pic"),
    TrackerColumns.editable("campaign", "Content PIC", "pics.content", "pic"),
    TrackerColumns.editable("campaign", "Notion", "links.notion", "link"),
  ];

  private static readonly SHIPPING_AND_BRIEF: TrackerColumn[] = [
    TrackerColumns.editable("content", "Brief Link", "phases.brief.link", "link"),
    TrackerColumns.editable("content", "Brief Due", "phases.brief.dueDate", "date"),
    TrackerColumns.editable("content", "Brief Status", "phases.brief.status", "status", STATUS.brief),
    TrackerColumns.editable("content", "Shipping Method", "shippingMethod", "text"),
    TrackerColumns.editable("content", "Shipping Link", "phases.shipping.link", "link"),
    TrackerColumns.editable("content", "Shipping Due", "phases.shipping.dueDate", "date"),
    TrackerColumns.editable("content", "Shipping Note", "phases.shipping.note", "longText"),
    TrackerColumns.editable("content", "Shipping Status", "phases.shipping.status", "status", STATUS.shipping),
    TrackerColumns.editable("content", "Content Tracker", "links.contentTracker", "link"),
  ];

  private static readonly POSTING_AND_REPORT: TrackerColumn[] = [
    TrackerColumns.computed("content", "Posted / Target", "posted"),
    TrackerColumns.computed("content", "Pace", "pace"),
    TrackerColumns.editable("content", "Posting Start", "phases.posting.startDate", "date"),
    TrackerColumns.editable("content", "Posting End", "phases.posting.dueDate", "date"),
    TrackerColumns.editable("content", "Posting Note", "phases.posting.note", "longText"),
    TrackerColumns.editable("content", "Posting Status", "phases.posting.status", "status", STATUS.posting),
    TrackerColumns.editable("content", "Report Link", "phases.report.link", "link"),
    TrackerColumns.editable("content", "Report Due", "phases.report.dueDate", "date"),
    TrackerColumns.editable("content", "Report Status", "phases.report.status", "status", STATUS.report),
  ];

  /** Same column order as the CAMPAIGN TRACKER [LIVE] tab of the master sheet. */
  public static readonly STANDARD: TrackerColumn[] = [
    ...TrackerColumns.CAMPAIGN_DETAILS,
    TrackerColumns.computed("onboarding", "Onboarded / Target", "onboarded"),
    TrackerColumns.editable("onboarding", "Onboarding Due", "phases.onboarding.dueDate", "date"),
    TrackerColumns.editable("onboarding", "Onboarding Note", "phases.onboarding.note", "longText"),
    TrackerColumns.editable("onboarding", "Final Creator List", "links.creatorList", "link"),
    TrackerColumns.editable("onboarding", "Onboarding Status", "phases.onboarding.status", "status", STATUS.onboarding),
    ...TrackerColumns.SHIPPING_AND_BRIEF,
    ...TrackerColumns.POSTING_AND_REPORT,
    TrackerColumns.editable("government", "Required?", "phases.government.required", "boolean"),
    TrackerColumns.editable("government", "Requirement", "phases.government.requirement", "longText"),
    TrackerColumns.editable("government", "Link", "phases.government.link", "link"),
    TrackerColumns.editable("government", "Due", "phases.government.dueDate", "date"),
    TrackerColumns.editable("government", "Status", "phases.government.status", "status", STATUS.government),
  ];

  /** Same columns as the PAID CAMPAIGN TRACKER tab; pay terms stay on the campaign reward. */
  public static readonly PAID: TrackerColumn[] = [
    ...TrackerColumns.CAMPAIGN_DETAILS,
    TrackerColumns.editable("onboarding", "Scouting Requirement", "scouting.requirement", "longText"),
    TrackerColumns.editable("onboarding", "Scouting Note", "scouting.note", "longText"),
    TrackerColumns.editable("onboarding", "Creator List", "links.creatorList", "link"),
    TrackerColumns.editable("onboarding", "Contract Template", "links.contract", "link"),
    TrackerColumns.editable("onboarding", "Onboarding Due", "phases.onboarding.dueDate", "date"),
    TrackerColumns.editable("onboarding", "Onboarding Status", "phases.onboarding.status", "status", STATUS.onboarding),
    TrackerColumns.editable("onboarding", "Payment Tracker", "links.paymentTracker", "link"),
    ...TrackerColumns.SHIPPING_AND_BRIEF,
    ...TrackerColumns.POSTING_AND_REPORT,
  ];

  public static isEditable(column: TrackerColumn): column is TrackerEditableColumn {
    return "field" in column;
  }
}
